/**
 * PaymentNotes.tsx — the PAYMENT notes thread inside the admin subscription
 * drawer (UX Wave 6 §3).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 (payments correctness).
 *
 * WHY: the payments drill-down used to render the CLINICAL notes thread
 * (clinicians/CaseNotes.tsx) — the wrong surface for admins in a payments
 * context (Wave-6 §3's critical fix). This thread carries payment-operations
 * chatter instead ("refund agreed", "card updated on the phone"), reusing the
 * SAME per-case notes API and composer grammar with the INTERIM `[payment]`
 * marker (lib/paymentNotes.ts): only marked notes render here, and the
 * clinical thread filters them OUT — clinical notes never appear in this
 * drawer, in any form. [BACKEND FOLLOW-UP] replace the marker with a true
 * kind='payment' on the orders-service case-notes store.
 *
 * patientVisible: NEVER offered here. When Wave-6 §8's "visible to patient"
 * compose flag lands on the shared NoteEditor, this composer MUST NOT grow
 * it — payment notes are internal admin operations chatter, never
 * patient-facing. (Guarded by a spec: no visibility control renders in the
 * payments drawer.)
 *
 * Authors resolve through the ADMIN batched lookup (name → email → raw id) —
 * this drawer is ADMIN-only (R41), so the lookup route is always available,
 * unlike the clinician-record read the clinical thread uses.
 */
import { useCallback, useEffect, useState } from 'react';
import { lookupStaffContactMap, type StaffContact } from '../api/identity';
import { addCaseNote, editCaseNote, getCaseNotes } from '../api/orders';
import type { CaseNote } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { formatUtcDate } from '../lib/format';
import { isPaymentNote, stripPaymentMarker, withPaymentMarker } from '../lib/paymentNotes';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { NoteEditor } from './callbacks/NoteEditor';
import { actionLinkStyle } from './callbacks/shared';
import { SectionTitle } from '../staff/ui';
import { Card, ErrorState, Help } from '../ui/kit';
import { DrawerSkeleton } from '../ui/skeletons';

type Loadable<T> = { state: 'loading' | 'ready' | 'error'; data: T | null };

export function PaymentNotesSection({ orderCaseId }: { orderCaseId: string }) {
  const { me } = useAuth();
  const [notes, setNotes] = useState<Loadable<CaseNote[]>>({ state: 'loading', data: null });
  // authorUserId → resolved contact. Best-effort: on failure rows show the raw id.
  const [authors, setAuthors] = useState<Record<string, StaffContact>>({});

  const load = useCallback(() => {
    setNotes({ state: 'loading', data: null });
    getCaseNotes(orderCaseId)
      .then(async (list) => {
        // The interim segregation: ONLY payment-marked notes render in the
        // payments drawer — the case's clinical notes never appear here.
        const paymentNotes = list.items.filter((n) => isPaymentNote(n.body));
        setNotes({ state: 'ready', data: paymentNotes });
        const ids = Array.from(new Set(paymentNotes.map((n) => n.authorUserId)));
        if (ids.length === 0) return;
        try {
          // ONE batched lookup for every author — never one per note.
          setAuthors(await lookupStaffContactMap(ids));
        } catch {
          // Best-effort — rows fall back to the raw author id.
        }
      })
      .catch(() => setNotes({ state: 'error', data: null }));
  }, [orderCaseId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <SectionTitle>Payment notes</SectionTitle>
      {notes.state === 'loading' ? (
        <DrawerSkeleton blocks={1} label="Loading payment notes…" />
      ) : notes.state === 'error' || !notes.data ? (
        <ErrorState message="Sorry — we could not load the payment notes." onRetry={load} />
      ) : (
        <>
          {notes.data.length === 0 ? (
            <Help style={{ marginBottom: 'var(--gps-s-3)' }}>No payment notes yet.</Help>
          ) : (
            <ul aria-label="Payment notes" style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '10px' }}>
              {/* Oldest first (defensively sorted, same grammar as the clinical
                  thread) — the newest note sits beside the composer. */}
              {[...notes.data]
                .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                .map((note) => (
                  <PaymentNoteRow
                    key={note.noteId}
                    note={note}
                    author={authors[note.authorUserId]}
                    canEdit={note.authorUserId === me?.userId}
                    orderCaseId={orderCaseId}
                    onChanged={load}
                  />
                ))}
            </ul>
          )}
          {/* NO patientVisible flag on this composer — ever (see the doc-block). */}
          <NoteEditor
            formLabel="Add a payment note"
            textboxLabel="New payment note"
            submitLabel="Add note"
            onSubmit={async (html) => {
              await addCaseNote(orderCaseId, withPaymentMarker(html));
              load();
            }}
          />
        </>
      )}
    </Card>
  );
}

function PaymentNoteRow({
  note,
  author,
  canEdit,
  orderCaseId,
  onChanged,
}: {
  note: CaseNote;
  author?: StaffContact;
  canEdit: boolean;
  orderCaseId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li>
        <NoteEditor
          formLabel="Edit payment note"
          textboxLabel="Payment note text"
          submitLabel="Save note"
          // Edit the CONTENT only — the marker is re-applied on save so the
          // note stays a payment note (the author never sees the token).
          initialHtml={stripPaymentMarker(note.body)}
          onSubmit={async (html) => {
            await editCaseNote(orderCaseId, note.noteId, withPaymentMarker(html));
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }
  return (
    <li style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
          {author?.fullName ?? author?.email ?? note.authorUserId}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--gps-muted)' }}>
          {formatUtcDate(note.createdAt)}
          {note.editedAt ? ' · edited' : ''}
        </span>
      </div>
      {/* Defence in depth (the server is the real sanitiser) — strip to a safe
          tag allowlist before ever rendering the note body as HTML. */}
      <div
        style={{ fontSize: '14px', color: 'var(--gps-text)' }}
        dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(stripPaymentMarker(note.body)) }}
      />
      {canEdit ? (
        <button type="button" onClick={() => setEditing(true)} style={{ ...actionLinkStyle, marginTop: '6px' }}>
          Edit
        </button>
      ) : null}
    </li>
  );
}
